import { QUADRANT, urgencyOpacity, type Quadrant, type Task } from '@cleartask/shared';
import { formatDueLabel, isOverdue } from '../../lib/datetime.js';
import { useTimeZone } from '../../session.js';
import { SnoozeMenu, type SnoozePayload } from './SnoozeMenu.js';
import { useSwipe } from './useSwipe.js';
import { useTagMap } from '../tags/useTags.js';

interface TaskCardProps {
  task: Task;
  now: number;
  onComplete: (task: Task) => void;
  onReopen: (task: Task) => void;
  onSnooze: (task: Task, payload: SnoozePayload) => void;
  onDelete: (task: Task) => void;
  onOpen: (task: Task) => void;
  /**
   * Aberta no detalhe. Com o painel fixo, lista e detalhe ficam visiveis ao
   * mesmo tempo, e sem a marca nada liga um ao outro.
   */
  selecionada?: boolean;
}

/**
 * Opacidade da barra de urgencia numa tarefa concluida.
 *
 * Fixa, e nao `urgencyOpacity`: uma tarefa vencida que foi FEITA nao esta mais
 * atrasada, e a barra em vermelho cheio afirmaria o contrario no meio do grupo
 * de executados. A cor do quadrante fica — ela ainda diz que tipo de trabalho
 * era —, so o volume cai.
 */
const OPACIDADE_CONCLUIDA = 0.25;

/** Cor da faixa de urgencia — a do quadrante (spec 7.1/7.3). */
const QUADRANT_COLOR: Record<Quadrant, string> = {
  [QUADRANT.URGENT_IMPORTANT]: 'var(--q1)',
  [QUADRANT.NOT_URGENT_IMPORTANT]: 'var(--q2)',
  [QUADRANT.URGENT_NOT_IMPORTANT]: 'var(--q3)',
  [QUADRANT.NEITHER]: 'var(--q4)',
};

/**
 * Card de tarefa.
 *
 * O elemento-assinatura (spec 7.3) e a **barra de urgencia**: 3 px na borda
 * esquerda, cor do quadrante, opacidade crescendo conforme o prazo se aproxima
 * (100% vencida, 30% a mais de 7 dias). Ela da leitura periferica do estado da
 * lista inteira sem ler texto nenhum — por isso a opacidade e calculada por
 * tarefa, e nao herdada de uma classe fixa.
 */
export function TaskCard({
  task,
  now,
  onComplete,
  onReopen,
  onSnooze,
  onDelete,
  onOpen,
  selecionada = false,
}: TaskCardProps) {
  const timeZone = useTimeZone();
  const concluida = task.status === 'done';
  // Uma tarefa concluida nunca esta vencida, mesmo que o prazo tenha passado
  // antes de ela ser feita: o prazo virou registro, nao cobranca. Pintar de
  // vermelho ali gastaria a cor que a convencao reserva a vencido/Q1.
  const vencida = !concluida && isOverdue(task.dueAt, now);
  const tagsPorId = useTagMap();

  // Uma tag apagada some do mapa mas pode sobreviver um instante em `tagIds`,
  // entre a exclusao e a revalidacao da lista. Filtrar aqui evita um chip
  // fantasma sem nome nem cor.
  const tags = task.tagIds.flatMap((id) => {
    const tag = tagsPorId.get(id);
    return tag ? [tag] : [];
  });

  // O swipe para a direita segue o checkbox: numa tarefa concluida ele reabre,
  // e nao conclui de novo. Sem isso o gesto teria efeito diferente do controle
  // acessivel equivalente, o que a convencao 8 nao admite.
  const { offset, swiping, handlers } = useSwipe({
    onSwipeRight: () => (concluida ? onReopen(task) : onComplete(task)),
    onSwipeLeft: () => onDelete(task),
  });

  const corDaFaixa = QUADRANT_COLOR[task.quadrant ?? QUADRANT.NEITHER];

  return (
    <li
      className="relative overflow-hidden border-b border-border last:border-b-0"
      // Ancora dos atalhos `E` (concluir) e `S` (adiar) da spec 10.2: eles agem
      // sobre o card que CONTEM o foco, encontrado por `closest`. Sem um
      // marcador no DOM, o atalho precisaria de um estado de "card selecionado"
      // paralelo ao foco do teclado — dois cursores na mesma tela, um deles
      // invisivel para quem usa leitor de tela.
      data-task-id={task.id}
      // `pan-y` deixa a rolagem vertical com o navegador e reserva o eixo
      // horizontal para o swipe — sem isso os dois competem no mobile.
      style={{ touchAction: 'pan-y' }}
      {...handlers}
    >
      <div
        className={[
          'flex items-center gap-3 px-3 py-2',
          selecionada ? 'bg-brand-blue-soft' : 'bg-surface',
        ].join(' ')}
        style={{
          transform: `translateX(${offset}px)`,
          // Sem transicao durante o gesto: o card precisa acompanhar o dedo em
          // tempo real. A transicao entra so no retorno a posicao.
          transition: swiping ? undefined : 'transform var(--dur) var(--ease)',
        }}
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-[3px]"
          style={{
            backgroundColor: corDaFaixa,
            opacity: concluida ? OPACIDADE_CONCLUIDA : urgencyOpacity(task.dueAt, now),
          }}
        />

        <input
          type="checkbox"
          checked={concluida}
          onChange={() => (concluida ? onReopen(task) : onComplete(task))}
          aria-label={concluida ? `Reabrir ${task.title}` : `Concluir ${task.title}`}
          className="h-5 w-5 shrink-0 accent-brand-teal"
        />

        {/*
          O risco no titulo e o mesmo do chip do calendario (`TaskChip`), de
          proposito: a mesma tarefa aparece nas duas telas, e dois tratamentos
          visuais para "feita" fariam a pessoa aprender a leitura duas vezes.
        */}
        <button
          type="button"
          onClick={() => onOpen(task)}
          aria-current={selecionada ? 'true' : undefined}
          /*
            `w-0` com `flex-1`: o titulo ocupa o que sobra, e nada mais. So o
            `truncate` cortava o texto na tela, mas a largura minima da linha
            continuava sendo o titulo INTEIRO (`nowrap`) — e ela sobe ate o
            <main>, que e item flex do shell. Um titulo longo esticava a pagina
            para 498 px num celular de 390 (pego pelo teste de navegador).
          */
          className={[
            'min-h-touch w-0 flex-1 truncate text-left text-base',
            concluida ? 'text-text-mut line-through' : '',
          ].join(' ')}
        >
          {task.title}
        </button>

        {/*
          Os chips ficam ENTRE o titulo e o prazo, e nao numa segunda linha: o
          card tem 44px de alvo de toque e uma linha extra por tarefa dobraria a
          altura da lista inteira por uma informacao secundaria.

          A cor entra como pontinho, nao como fundo do chip: cor de tag e
          escolhida livremente pelo usuario e nao passa por checagem de
          contraste — texto sobre ela reprovaria em AA sem aviso (spec 10.5).
        */}
        {tags.length > 0 ? (
          <span className="hidden shrink-0 items-center gap-1 sm:flex">
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="flex items-center gap-1 rounded-sm bg-surface-2 px-1.5 py-0.5 text-xs text-text-mut"
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
              </span>
            ))}
          </span>
        ) : null}

        {task.dueAt !== null ? (
          <span
            className={[
              'shrink-0 text-sm tabular-nums',
              vencida ? 'font-medium text-overdue' : 'text-text-mut',
            ].join(' ')}
          >
            {formatDueLabel(task.dueAt, task.allDay, now, timeZone)}
          </span>
        ) : null}

        {/*
          O sino do wireframe da spec 9.1. So na tarefa ABERTA: o cron de
          lembretes filtra `status = 'open'`, entao o aviso de uma tarefa
          concluida nunca sai, e o icone anunciaria algo que nao vai acontecer.
        */}
        {task.reminderAt !== null && !concluida ? (
          <span role="img" aria-label={`${task.title} tem lembrete`} className="shrink-0 text-sm">
            ⏰
          </span>
        ) : null}

        {/*
          Adiar some na tarefa concluida: os quatro presets produzem um prazo
          FUTURO (a invariante de `snooze.ts`), e empurrar o prazo de algo que
          ja foi feito nao descreve nenhuma intencao. Excluir fica — mandar uma
          tarefa concluida para a lixeira continua valendo.
        */}
        {concluida ? null : (
          <SnoozeMenu label="Adiar" onSelect={(payload) => onSnooze(task, payload)} />
        )}

        <button
          type="button"
          onClick={() => onDelete(task)}
          aria-label={`Excluir ${task.title}`}
          className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2 hover:text-overdue"
        >
          ✕
        </button>
      </div>
    </li>
  );
}
